/**
 * Robust Authentication Context
 * 
 * ANTI-FRAGILITY DESIGN PRINCIPLES:
 * 1. FAIL-FAST: Throw errors immediately when auth is missing/broken
 * 2. SINGLE SOURCE: One auth context for entire app - no optional setters
 * 3. AUTO-INITIALIZATION: No manual setup required - works or fails clearly
 * 4. VALIDATION: Built-in checks prevent silent failures
 * 5. RECOVERY: Automatic token refresh and error recovery
 * 
 * PREVENTS THESE REGRESSIONS:
 * - APIs silently failing with empty results
 * - Missing token setup causing 401 errors
 * - Group search returning "No groups found" when auth is broken
 * - Validation getting bypassed "for testing"
 */

import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useMSALAuth } from './MSALAuthContext';
import { setAuthContextForApi } from '../services/robustApiInstance';

interface AuthState {
  isAuthenticated: boolean;
  account: any | null; // We don't use this in the robust system
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  isTeamsAdmin: boolean;
}

interface AuthContextValue extends AuthState {
  login: () => Promise<void>;
  logout: () => void;
  refreshTokens: () => Promise<void>;
  getAccessToken: () => Promise<string>;
  validateAuth: () => void; // Throws if auth is invalid
}

interface RobustAuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const RobustAuthProvider: React.FC<RobustAuthProviderProps> = ({ children }) => {
  const msalAuth = useMSALAuth();

  // FAIL-FAST: Validate authentication state
  const validateAuth = (): void => {
    if (!msalAuth.isAuthenticated) {
      throw new Error('AUTHENTICATION_REQUIRED: User is not authenticated. This indicates a broken auth flow.');
    }
    if (!msalAuth.token) {
      throw new Error('ACCESS_TOKEN_MISSING: No access token available. This will cause all API calls to fail with 401.');
    }
    if (!msalAuth.user) {
      throw new Error('ACCOUNT_INFO_MISSING: No account information available. Cannot proceed with authenticated operations.');
    }
  };

  // ROBUST: Get access token from existing MSAL auth
  const getAccessToken = async (): Promise<string> => {
    validateAuth();
    if (!msalAuth.token) {
      throw new Error('ACCESS_TOKEN_MISSING: No access token available from MSAL auth');
    }
    return msalAuth.token;
  };

  // Configure robust API when MSAL auth is ready
  const configureRobustApi = () => {
    try {
      if (msalAuth.isAuthenticated && msalAuth.token) {
        // Configure the robust API client with MSAL auth
        setAuthContextForApi({
          getAccessToken: getAccessToken,
          refreshTokens: msalAuth.refreshUser,
          validateAuth: validateAuth
        });

        // Only log in development
        if (process.env.NODE_ENV === 'development') {
          console.log('✅ ROBUST AUTH: Authentication configured successfully', {
            hasAccessToken: !!msalAuth.token,
            isTeamsAdmin: msalAuth.user?.isTeamsAdmin,
            apiClientsConfigured: true
          });
        }
      }
    } catch (error) {
      console.error('❌ ROBUST AUTH: Authentication configuration failed:', error);
    }
  };

  // Configure robust API when MSAL auth changes
  useEffect(() => {
    configureRobustApi();
  }, [msalAuth.isAuthenticated, msalAuth.token]);

  const contextValue: AuthContextValue = {
    isAuthenticated: msalAuth.isAuthenticated,
    account: null, // We don't need this for the robust system
    accessToken: msalAuth.token,
    isLoading: msalAuth.isLoading,
    error: msalAuth.error?.message || null,
    isTeamsAdmin: msalAuth.user?.isTeamsAdmin || false,
    login: msalAuth.login,
    logout: msalAuth.logout,
    refreshTokens: msalAuth.refreshUser,
    getAccessToken,
    validateAuth
  };

  return (
    <AuthContext.Provider value={contextValue}>
      <span hidden data-testid="robust-auth-provider" />
      {children}
    </AuthContext.Provider>
  );
};

// FAIL-FAST Hook: Throws error if auth context is not available
export const useRobustAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(
      'CONTEXT_ERROR: useRobustAuth must be used within a RobustAuthProvider. ' +
      'This indicates the auth provider was not properly set up in the component tree.'
    );
  }
  return context;
};

// VALIDATION Hook: Ensures auth is valid before proceeding
export const useValidatedAuth = (): AuthContextValue => {
  const auth = useRobustAuth();
  auth.validateAuth(); // Throws if auth is invalid
  return auth;
};

export default RobustAuthProvider;